import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconHandStop,
    IconMaximize,
    IconMinus,
    IconPhotoScan,
    IconPlus,
    IconPointer,
    IconRestore,
    IconRotate,
    IconRotateClockwise,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import useImagePaste from '../../components/image/useImagePaste';
import { downloadBlob, getToolStatusText } from '../../toolPageUtils';
import PhotoSplitStage from './PhotoSplitStage';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import PhotoBoothInbox from '../../../../../components/common/PhotoBoothInbox';
import Hint from '../../../../../components/common/Hint';
import AiToolLink from '../../components/common/AiToolLink';
import { cropRect, cropToBlob } from './photoSplitEngine';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_FILE_BYTES = 40 * 1024 * 1024;

/**
 * Cut one picture into the pieces somebody marks on it.
 *
 * Laid out the way the other image tools are: the picture being worked on
 * fills the preview panel, and everything that acts on it sits in the column
 * beside it. Nothing is guessed and nothing is written until the download is
 * asked for, so what is in the list is exactly what lands on the disk.
 */
export default function PhotoSplitImage() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const digits = useCallback((n) => (isBn ? toBnDigits(n) : String(n)), [isBn]);

    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [image, setImage] = useState(null);
    const [selections, setSelections] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(null); // open piece in the full-screen preview
    const [busy, setBusy] = useState(false);
    const [idleDragging, setIdleDragging] = useState(false);
    const [format, setFormat] = useState('original');
    // Zoom is the preview's own, not the document's: a piece marked on a phone
    // photo is a few dozen pixels on screen until you magnify it.
    const [zoom, setZoom] = useState(1);
    // Marking and moving about are the same gesture, so which one it is has to
    // be a choice. Making it implicit — pan once magnified — meant you could
    // never mark a piece at the zoom where you most want to.
    const [panMode, setPanMode] = useState(false);
    // Space is hold-to-pan, the way every canvas app does it: it borrows the
    // mode for as long as it is down and gives it straight back, so moving
    // about never costs a trip to the toolbar and back.
    const [spaceHeld, setSpaceHeld] = useState(false);
    const panning = panMode || spaceHeld;
    // Left-column tabs: the tool's own settings, or the photo booth queue. The
    // booth tab only exists while the booth is on.
    const [sideTab, setSideTab] = useState('settings');
    const [boothOn, setBoothOn] = useState(false);

    const selected = selections.find((s) => s.id === selectedId) || null;

    const reset = useCallback(() => {
        setFile(null);
        setImage(null);
        setSelections([]);
        setSelectedId(null);
    }, []);

    const load = useCallback(async (picked) => {
        if (!picked) return;
        if (!picked.type?.startsWith('image/')) {
            notifications.show({ title: t('common.error'), message: t('photoSplit.errors.type'), color: 'red' });
            return;
        }
        if (picked.size > MAX_FILE_BYTES) {
            notifications.show({ title: t('common.error'), message: t('photoSplit.errors.tooLarge'), color: 'red' });
            return;
        }
        try {
            const img = new Image();
            img.src = URL.createObjectURL(picked);
            await img.decode();
            setFile(picked);
            setImage(img);
            setSelections([]);
            setSelectedId(null);
            setZoom(1);
        } catch {
            notifications.show({ title: t('common.error'), message: t('photoSplit.errors.failed'), color: 'red' });
        }
    }, [t]);

    useImagePaste((pastedFiles) => load(pastedFiles?.[0]));

    /* ------------------------------------------------------------- cut-outs */

    // Re-cut on every change, so the list is never a picture of an older box.
    const crops = useMemo(() => {
        if (!image) return [];
        return selections.map((s, i) => {
            try {
                const canvas = cropRect(image, s);
                return { id: s.id, index: i, url: canvas.toDataURL('image/jpeg', 0.9), w: canvas.width, h: canvas.height };
            } catch {
                return null;
            }
        }).filter(Boolean);
    }, [image, selections]);

    const baseName = (file?.name || 'image').replace(/\.[^.]+$/, '');

    // Before/after items for the shared compare viewer: "before" is the whole uploaded
    // picture, "after" is the cut-out piece. Keyed by the crop id so prev/next and
    // download map back to the right selection.
    const compareItems = useMemo(() => crops.map((c) => ({
        id: c.id,
        uploadedImage: image?.src || '',
        aiImage: c.url,
        fileName: `${baseName}-${c.index + 1}`,
        mimeType: 'image/jpeg',
    })), [crops, image, baseName]);

    const downloadOne = useCallback(async (index) => {
        const type = file?.type || '';
        const { blob, ext } = await cropToBlob(cropRect(image, selections[index]), format, type);
        if (blob) downloadBlob(blob, `${baseName}-${index + 1}.${ext}`);
    }, [image, selections, baseName, format, file]);

    const downloadAll = useCallback(async () => {
        if (!selections.length) return;
        setBusy(true);
        try {
            if (selections.length === 1) {
                await downloadOne(0);
            } else {
                // `zipImageFiles` wants `{ name, blob }`, not File objects.
                // Handing it Files put `undefined` in every entry, and the zip
                // came out full of pictures that would not open.
                const entries = [];
                for (let i = 0; i < selections.length; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const { blob, ext } = await cropToBlob(cropRect(image, selections[i]), format, file?.type);
                    if (blob) entries.push({ name: `${baseName}-${i + 1}.${ext}`, blob });
                }
                downloadBlob(await zipImageFiles(entries), `${baseName}-pieces.zip`);
            }
            recordToolUsage('image/photo-split');
        } catch {
            notifications.show({ title: t('common.error'), message: t('photoSplit.errors.failed'), color: 'red' });
        } finally {
            setBusy(false);
        }
    }, [selections, image, downloadOne, baseName, format, file, t]);

    const patchSelected = useCallback((patch) => {
        if (!selectedId) return;
        setSelections((prev) => prev.map((s) => (s.id === selectedId ? { ...s, ...patch } : s)));
    }, [selectedId]);

    const removeOne = useCallback((id) => {
        setSelections((prev) => prev.filter((s) => s.id !== id));
        setSelectedId((prev) => (prev === id ? null : prev));
    }, []);

    useEffect(() => {
        const typing = () => {
            const tag = document.activeElement?.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
        };
        const onKeyDown = (e) => {
            if (typing()) return;
            if (e.code === 'Space' || e.key === ' ') {
                // Space scrolls the page by default, which is the one thing it
                // must not do while it is being used to move the picture.
                e.preventDefault();
                setSpaceHeld(true);
                return;
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                e.preventDefault();
                removeOne(selectedId);
            }
        };
        const onKeyUp = (e) => {
            if (e.code === 'Space' || e.key === ' ') setSpaceHeld(false);
        };
        // A window that loses focus mid-hold never sees the keyup, and the
        // canvas would be stuck panning until Space was pressed again.
        const release = () => setSpaceHeld(false);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', release);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', release);
        };
    }, [selectedId, removeOne]);

    useEffect(() => () => { if (image?.src?.startsWith('blob:')) URL.revokeObjectURL(image.src); }, [image]);

    const status = !file ? 'empty' : selections.length ? 'success' : 'ready';

    return (
        <>
        <ToolPageLayout
            slug="image/photo-split"
            subtitleKey="photoSplit.subtitle"
            status={status}
            statusText={getToolStatusText(status, t, {
                empty: t('photoSplit.status.empty'),
                ready: t('photoSplit.status.ready'),
                success: t('photoSplit.status.marked', { count: digits(selections.length) }),
            })}
            onReset={file ? reset : undefined}
            previewFirst
            showTrust={false}
            primaryAction={selections.length ? {
                label: selections.length > 1 ? t('photoSplit.downloadZip') : t('photoSplit.download'),
                onClick: downloadAll,
                icon: IconDownload,
                loading: busy,
            } : {
                label: t('photoSplit.pick'),
                onClick: () => fileInputRef.current?.click(),
                icon: IconPhotoScan,
                disabled: !!image,
            }}
            secondaryAction={selections.length ? {
                label: t('photoSplit.clearMarks'),
                onClick: () => { setSelections([]); setSelectedId(null); },
                icon: IconX,
            } : undefined}
            settings={(
                <div className="space-y-3">
                {/* One white panel for the tool itself — the selected piece and
                    the output format are sections of it divided by hairlines,
                    rather than a stack of separate cards. The way across to the
                    AI splitter sits under it, outside the panel: it leaves this
                    tool rather than changing it. */}
                <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                    {/* The picture is chosen in the preview panel, so this
                        column carries what acts on it — this input is what the
                        empty state opens. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { load(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    <div>
                        {boothOn && (
                            <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
                                {[
                                    { id: 'settings', label: t('photoSplit.settingsTab') },
                                    { id: 'booth', label: t('photoCollectorPanel.title') },
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setSideTab(tab.id)}
                                        className={`rounded-md py-1.5 text-xs font-semibold transition-colors ${
                                            sideTab === tab.id
                                                ? 'bg-white text-primary shadow-sm dark:bg-zinc-900'
                                                : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* Kept mounted while hidden, so the booth's on/off
                            status resolves whichever tab is showing. */}
                        <div className={boothOn && sideTab === 'booth' ? 'mt-3' : 'hidden'}>
                            {/* Single-pick mode calls `onPickFile`; `onPickFiles`
                                only ever fires with `multiSelect`, so wiring that
                                one here meant a tapped booth photo went nowhere. */}
                            <PhotoBoothInbox
                                onPickFile={(picked) => {
                                    load(picked);
                                    setSideTab('settings');
                                }}
                                onStatusChange={setBoothOn}
                            />
                        </div>
                    </div>

                    <div className={boothOn && sideTab === 'booth' ? 'hidden' : `space-y-4 ${boothOn ? 'mt-4' : ''}`}>
                        <section>
                            <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('photoSplit.pieceTitle')}
                            </p>
                            {!selected ? (
                                <p className="text-[12.5px] leading-relaxed text-[#A8A099] dark:text-zinc-400">
                                    {image ? t('photoSplit.pieceNone') : t('photoSplit.previewNoFile')}
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                            {t('photoSplit.rotation')}
                                        </span>
                                        <span className="text-[12.5px] font-semibold text-amber-600">
                                            {digits(selected.rotation || 0)}°
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={-180}
                                        max={180}
                                        step={1}
                                        value={selected.rotation || 0}
                                        onChange={(e) => patchSelected({ rotation: Number(e.target.value) })}
                                        aria-label={t('photoSplit.rotation')}
                                        className="w-full accent-amber-500"
                                    />
                                    {/* Icons only, filling the row: four one-word
                                        actions read as a paragraph of buttons. */}
                                    <div className="flex w-full items-center gap-1.5">
                                        <IconButton
                                            icon={IconRotate}
                                            label={t('photoSplit.rotateLeft')}
                                            onClick={() => patchSelected({ rotation: ((selected.rotation || 0) - 90 + 540) % 360 - 180 })}
                                        />
                                        <IconButton
                                            icon={IconRotateClockwise}
                                            label={t('photoSplit.rotateRight')}
                                            onClick={() => patchSelected({ rotation: ((selected.rotation || 0) + 90 + 540) % 360 - 180 })}
                                        />
                                        <IconButton
                                            icon={IconRestore}
                                            label={t('photoSplit.straighten')}
                                            onClick={() => patchSelected({ rotation: 0 })}
                                            disabled={!selected.rotation}
                                        />
                                        <IconButton
                                            icon={IconTrash}
                                            label={t('photoSplit.removePiece')}
                                            tone="danger"
                                            onClick={() => removeOne(selected.id)}
                                        />
                                    </div>
                                </div>
                            )}
                        </section>

                        {crops.length ? (
                            <section className="border-t border-[#F0EEEB] pt-4 dark:border-zinc-800">
                                <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('photoSplit.previewTitle')}
                                </p>
                                {/* Three rows, then it scrolls: a long list
                                    would push the format picker off the column. */}
                                <div className="flex max-h-[204px] flex-col gap-1.5 overflow-y-auto pr-0.5 [scrollbar-width:thin]">
                                    {crops.map((crop, i) => (
                                        <div
                                            key={crop.id}
                                            className={`flex items-center gap-2.5 rounded-xl border p-1.5 transition-colors ${
                                                crop.id === selectedId
                                                    ? 'border-primary bg-primary-50/50 dark:bg-primary-950/20'
                                                    : 'border-[#ECE8E4] hover:border-primary/40 dark:border-zinc-700'
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setPreviewIndex(i)}
                                                aria-label={t('photoSplit.photoN', { n: digits(crop.index + 1) })}
                                                className="group relative h-[52px] w-[52px] flex-none overflow-hidden rounded-lg bg-white dark:bg-zinc-800"
                                            >
                                                <img src={crop.url} alt="" className="h-full w-full object-contain" />
                                                <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white/0 transition-all group-hover:bg-black/40 group-hover:text-white">
                                                    <IconMaximize size={16} />
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedId(crop.id)}
                                                className="flex min-w-0 flex-1 items-center text-left"
                                            >
                                                <span className="min-w-0">
                                                    <span className="block text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-100">
                                                        {t('photoSplit.photoN', { n: digits(crop.index + 1) })}
                                                    </span>
                                                    <span className="block text-[11.5px] text-[#A8A099] dark:text-zinc-400">
                                                        {digits(crop.w)} × {digits(crop.h)}
                                                    </span>
                                                </span>
                                            </button>
                                            <Hint label={t('photoSplit.download')}>
                                                <button
                                                    type="button"
                                                    onClick={() => downloadOne(crop.index)}
                                                    aria-label={t('photoSplit.download')}
                                                    className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-primary dark:hover:bg-zinc-800"
                                                >
                                                    <IconDownload size={15} stroke={1.8} />
                                                </button>
                                            </Hint>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <div className="border-t border-[#F0EEEB] pt-4 dark:border-zinc-800">
                            <OutputFormatPicker
                                bare
                                value={format}
                                onChange={setFormat}
                                transparentOutput
                            />
                        </div>
                    </div>
                </div>
                <AiToolLink
                    to="/tools/image/ai-photo-split"
                    titleKey="photoSplit.aiLink.title"
                    bodyKey="photoSplit.aiLink.body"
                />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('photoSplit.stageTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    headerAction={image ? (
                        // The same pill the document tools use: step the zoom,
                        // read it, and get back to a whole-picture view.
                        <span className="flex items-center gap-1 rounded-xl border border-[#ECE8E4] bg-white/95 p-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/95">
                            <ZoomButton
                                icon={panning ? IconPointer : IconHandStop}
                                label={panMode ? t('photoSplit.markMode') : t('photoSplit.panMode')}
                                hint={t('photoSplit.panShortcut')}
                                onClick={() => setPanMode((on) => !on)}
                                active={panning}
                            />
                            <span className="mx-0.5 h-5 w-px flex-none bg-[#ECE8E4] dark:bg-zinc-700" />
                            <ZoomButton
                                icon={IconMinus}
                                label={t('photoSplit.zoomOut')}
                                onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}
                                disabled={zoom <= 0.5}
                            />
                            <span className="min-w-[44px] text-center text-[12px] font-semibold tabular-nums text-[#3D3833] dark:text-zinc-200">
                                {digits(Math.round(zoom * 100))}%
                            </span>
                            <ZoomButton
                                icon={IconPlus}
                                label={t('photoSplit.zoomIn')}
                                onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.25) * 100) / 100))}
                                disabled={zoom >= 4}
                            />
                            <ZoomButton
                                icon={IconMaximize}
                                label={t('photoSplit.fitToScreen')}
                                onClick={() => setZoom(1)}
                                disabled={zoom === 1}
                            />
                        </span>
                    ) : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={(e) => {
                        e.preventDefault();
                        setIdleDragging(false);
                        load(e.dataTransfer?.files?.[0]);
                    }}
                    onIdleDragOver={(e) => { e.preventDefault(); setIdleDragging(true); }}
                    onIdleDragLeave={() => setIdleDragging(false)}
                    idleDragging={idleDragging}
                >
                    {image ? (
                        <div className="p-2">
                            <PhotoSplitStage
                                image={image}
                                zoom={zoom}
                                selections={selections}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                                onChange={setSelections}
                                panMode={panning}
                            />
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        <ImagePreviewModal
            opened={previewIndex !== null}
            onClose={() => setPreviewIndex(null)}
            images={compareItems}
            initialImage={previewIndex !== null ? compareItems[previewIndex] : null}
            onDownloadImage={(img) => { const i = compareItems.findIndex((it) => it.id === img?.id); if (i >= 0) downloadOne(crops[i].index); }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}

function ZoomButton({ icon: Icon, label, onClick, disabled, active, hint }) {
    return (
        <Hint label={hint ? `${label} — ${hint}` : label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={active}
                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${
                    active
                        ? 'bg-primary-50 text-primary dark:bg-primary-950/40'
                        : 'text-[#3D3833] hover:bg-[#F7F5F3] dark:text-zinc-200 dark:hover:bg-zinc-800'
                }`}
            >
                <Icon size={15} stroke={2} />
            </button>
        </Hint>
    );
}

function IconButton({ icon: Icon, label, onClick, disabled, tone, compact, active }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={active}
                className={`inline-flex items-center justify-center rounded-lg border transition-colors disabled:opacity-40 ${
                    compact ? 'h-7 w-7 flex-none' : 'h-9 flex-1'
                } ${
                    active
                        ? 'border-primary bg-primary-50 text-primary dark:border-primary/50 dark:bg-primary-950/30'
                        : tone === 'danger'
                        ? 'border-[#ECE8E4] bg-white text-[#6B635C] hover:border-[#D33A2C]/40 hover:bg-[#FDECEA] hover:text-[#D33A2C] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400'
                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 hover:bg-primary-50/60 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                }`}
            >
                <Icon size={compact ? 14 : 16} stroke={1.8} />
            </button>
        </Hint>
    );
}
